import { useState } from "react";
import { api } from "../../lib/api";
import { Search } from "lucide-react";
import { RegenerateVisualsButton } from "./RegenerateVisualsButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");

export function QuoteSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vmap, setVmap] = useState({}); // id -> cache-bust version after a re-roll

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/quotes/search?q=${encodeURIComponent(q.trim())}`);
      setItems(r.data.items || []);
      setSearched(true);
    } catch (e) { console.debug("Quote search failed:", e); }
    setLoading(false);
  };

  const onRegenerated = (id) => (data) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, photo_url: data.photo_url || it.photo_url } : it));
    setVmap(prev => ({ ...prev, [id]: Date.now() }));
  };

  return (
    <div data-testid="quote-search" className="border border-[#E5E7EB] bg-white p-4">
      <h2 className="text-lg font-bold tracking-tight mb-1">Find a quote</h2>
      <p className="text-xs text-[#6B7280] mb-3">Search saved quotes by reference, name, email or postcode — handy when a client calls in.</p>
      <div className="flex gap-2">
        <input data-testid="quote-search-input" value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") run(); }}
          placeholder="e.g. Q-7F3K2A, GU11, jane@…" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
        <button data-testid="quote-search-btn" onClick={run} disabled={loading} className="de-btn-primary text-xs inline-flex items-center gap-2"><Search size={14} /> {loading ? "Searching…" : "Search"}</button>
      </div>

      {searched && (
        <div className="mt-4 space-y-3" data-testid="quote-search-results">
          {items.length === 0 && <div className="text-sm text-[#6B7280]">No matching quotes.</div>}
          {items.map(it => (
            <div key={it.id} data-testid={`quote-result-${it.reference || it.id}`} className="flex items-center gap-4 border border-[#E5E7EB] p-3">
              {it.photo_url
                ? <img src={`${BACKEND}${it.photo_url}${vmap[it.id] ? `?v=${vmap[it.id]}` : ""}`} alt="" loading="lazy" onError={(e) => { e.target.style.visibility = "hidden"; }} className="w-24 h-16 object-cover border border-[#E5E7EB] bg-[#F4F4F4] flex-shrink-0" />
                : <div className="w-24 h-16 bg-[#F4F4F4] border border-[#E5E7EB] flex-shrink-0 flex items-center justify-center text-[10px] text-[#6B7280]">No image</div>}
              <div className="flex-1 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-[#C79A3B]">{it.reference || "—"}</span>
                  <span className="text-[10px] uppercase tracking-widest bg-[#F4F4F4] border border-[#E5E7EB] px-2 py-0.5 rounded-full">{it.route === "planning" ? "Planning" : "Permitted dev"}</span>
                  {it.expired
                    ? <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`quote-expired-${it.reference || it.id}`}>Expired</span>
                    : <span className="text-[10px] uppercase tracking-widest bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Valid</span>}
                </div>
                <div className="text-[#6B7280] text-xs mt-1">{it.name || "—"} · {it.email || "no email"} · {it.postcode || "—"}</div>
              </div>
              <div className="text-right text-sm flex flex-col items-end gap-2">
                <div>
                  <div className="font-bold">{gbp(it.total_inc_vat)}</div>
                  {it.valid_until && <div className="text-[11px] text-[#6B7280]">until {new Date(it.valid_until).toLocaleDateString("en-GB")}</div>}
                </div>
                <RegenerateVisualsButton quoteId={it.id} onDone={onRegenerated(it.id)} small
                  testid={`quote-regenerate-${it.reference || it.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
